import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { workorderApi } from '../api/workorderApi'
import { InventoryItem } from './types'
import { WorkOrderState } from './types'
import { appStore } from '@/app/store/appStore'
import { RootState } from '@/app/store/appStore'
import { WorkOrder, WorkOrderItem } from '../api/types'
import { normalizeItemsFromRes } from '@/shared/helpers/normalize/normalizeItemsFromRes'

const initialState: WorkOrderState = {
  data: {
    operations_manager_id: null,
    event_name: null,
    start_date: null,
    end_date: null,
    event_location: null,
    is_draft: false,
    status: 'open',
    created_by: 0,
    updated_at: '',
    created_at: '',
    id: 0,
    items: {},
    deletedItems: [],
  },
  loading: false,
  error: null,
  step: 1,
}

type SetFieldValuePayload<T> = {
  name: keyof T
  value: T[keyof T]
}

const workOrderSlice = createSlice({
  name: 'workOrder',
  initialState,
  reducers: {
    setInitState(state) {
      state.data = initialState.data
      state.error = null
      state.loading = false
      state.step = 1
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    setFieldValue(
      state,
      { payload }: PayloadAction<SetFieldValuePayload<WorkOrderState['data']>>,
    ) {
      state.data[payload.name] = payload.value
    },

    setInventoryItemQty(state, { payload }: PayloadAction<InventoryItem>) {
      state.data.items[payload.asset_type_id].amount = payload.amount
    },
    setInventoryItemType(
      state,
      {
        payload,
      }: PayloadAction<{
        itemId: number
        permanentId: number
        available_amount: number
        name: string
      }>,
    ) {
      const { itemId, permanentId, available_amount, name } = payload

      if (state.data.items[permanentId]) {
        const oldItem = state.data.items[permanentId]
        delete state.data.items[permanentId]

        state.data.items[itemId] = {
          ...oldItem,
          asset_type_id: itemId,
          id: itemId,
          available_amount,
          name,
        }
      }
    },
    addInventoryItem(state, action: PayloadAction<InventoryItem>) {
      state.data.items = {
        ...state.data.items,
        [action.payload.asset_type_id]: action.payload,
      }
    },
    removeInventoryItem(state, action: PayloadAction<number>) {
      delete state.data.items[action.payload]
    },
    setInventoryDeletedItemIds(state, action: PayloadAction<number>) {
      state.data.deletedItems = [...state.data.deletedItems, action.payload]
    },
    removeInventoryDeletedItemIds(state, action: PayloadAction<number>) {
      state.data.deletedItems = state.data.deletedItems.filter(
        item => item !== action.payload,
      )
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        workorderApi.endpoints.getWorkOrderDetail.matchPending,
        (state, action) => {
          state.loading = true
          state.error = null
          state.data = initialState.data
        },
      )
      .addMatcher(
        workorderApi.endpoints.getWorkOrderDetail.matchFulfilled,
        (state, action: PayloadAction<WorkOrder>) => {
          state.loading = false
          state.data = {
            ...action.payload,
            items: normalizeItemsFromRes(action.payload.items),
          }
        },
      )
      .addMatcher(
        workorderApi.endpoints.getWorkOrderDetail.matchRejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        },
      )
  },
})

const selectItems = (state: RootState) => state.workOrder.data.items

const selectFormData = (state: RootState): WorkOrderState['data'] =>
  state.workOrder.data
export const selectDeletedItems = (state: RootState) =>
  state.workOrder.data.deletedItems

export const selectInventoryItems = createSelector([selectItems], items =>
  Object.values(items),
)

export const selectInventoryItemsByCategory = (categoryId: number) =>
  createSelector([selectItems], (items: WorkOrderItem) =>
    Object.values(items)
      .filter(item => item.asset_category_id === categoryId)
      .sort((a, b) => a.index - b.index),
  )

export const formDataSelector = createSelector([selectFormData], data => data)

export const {
  setLoading,
  setError,
  setInventoryItemQty,
  setInventoryItemType,
  addInventoryItem,
  removeInventoryItem,
  setFieldValue,
  setInventoryDeletedItemIds,
  removeInventoryDeletedItemIds,
  setInitState,
} = workOrderSlice.actions

export default workOrderSlice
